import { analyticsConfig } from "../config/analytics";
import { QUESTION_COUNT, questionnaireQuestions } from "../data/questionnaire";

type Tag = (...args: unknown[]) => void;
type ClarityTag = Tag & { q?: unknown[][] };

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Tag;
    clarity?: ClarityTag;
  }
}

const pageTitles = {
  "/": "Evolution by Corina | Nutrición y entrenamiento personalizado",
  "/cuestionario": "Cuestionario | Evolution by Corina",
  "/privacidad": "Política de privacidad | Evolution by Corina",
  "/aviso-legal": "Aviso legal | Evolution by Corina",
} as const;
type PagePath = keyof typeof pageTitles;
const ctaDestinations = {
  header: "/cuestionario",
  mobile_menu: "/cuestionario",
  hero: "/cuestionario",
  final: "/cuestionario",
  hero_method: "#metodo",
} as const;
export type CtaId = keyof typeof ctaDestinations;
export type FormErrorType = "network" | "http" | "invalid_response";
type EventName = "page_view" | "cta_click" | "form_started" | "question_viewed"
  | "question_answered" | "form_contact_started" | "form_completed" | "form_error";

let initialized = false;
let clarityScheduled = false;
let lastPage: PagePath | undefined;

// Tracking must never become a dependency of navigation, validation or submission.
function safely(action: () => void): void {
  try { action(); } catch { /* Disabled storage, ad blockers and vendor failures are harmless. */ }
}

function hasGaId(): boolean {
  return /^G-[A-Z0-9]+$/.test(analyticsConfig.gaMeasurementId)
    && analyticsConfig.gaMeasurementId !== "G-XXXXXXXXXX";
}

function pagePath(): PagePath | undefined {
  // React Router matches these route aliases as well. Always emit the canonical path.
  const pathname = window.location.pathname.toLowerCase().replace(/\/+$/, "") || "/";
  return Object.hasOwn(pageTitles, pathname) ? pathname as PagePath : undefined;
}

function pageContext(path: PagePath) {
  return {
    page_path: path,
    page_title: pageTitles[path],
    // Never forward arbitrary query strings, fragments, referrers or document titles.
    page_location: `${window.location.origin}${path}`,
    page_referrer: "",
  };
}

function addScript(id: string, src: string): void {
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.src = src;
  script.async = true;
  script.referrerPolicy = "no-referrer";
  script.onerror = () => { /* No retries or UI errors when a tracker is blocked. */ };
  document.head.appendChild(script);
}

function canRecordLanding(): boolean {
  // Full document navigation to the questionnaire is intentional: masking does not
  // hide radio selection/click coordinates. Clarity never runs in that document.
  if (window.location.pathname !== "/" || window.location.search || window.location.hash) return false;
  if (!document.referrer) return true;
  try {
    const referrer = new URL(document.referrer);
    if (referrer.search || referrer.hash || referrer.username || referrer.password) return false;
    return referrer.origin === window.location.origin
      ? Object.hasOwn(pageTitles, referrer.pathname)
      : referrer.pathname === "/";
  } catch { return false; }
}

export function initializeAnalytics(): void {
  safely(() => {
    if (initialized) return;
    initialized = true;
    if (hasGaId()) {
      window.dataLayer = window.dataLayer || [];
      window.gtag = window.gtag || function () { window.dataLayer?.push(arguments); };
      const path = pagePath() ?? "/";
      window.gtag("js", new Date());
      window.gtag("set", pageContext(path));
      window.gtag("config", analyticsConfig.gaMeasurementId, {
        ...pageContext(path),
        send_page_view: false,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      });
      addScript("evolution-ga4", `https://www.googletagmanager.com/gtag/js?id=${analyticsConfig.gaMeasurementId}`);
    }
  });
  // Separate failure boundaries: one vendor cannot prevent the other from loading.
  safely(() => {
    if (clarityScheduled || !/^[a-z0-9]+$/.test(analyticsConfig.clarityProjectId) || !canRecordLanding()) return;
    clarityScheduled = true;
    const loadClarity = () => safely(() => {
      if (!canRecordLanding() || document.getElementById("evolution-clarity")) return;
      window.clarity = window.clarity || Object.assign(function (...args: unknown[]) {
        window.clarity?.q?.push(args);
      }, { q: [] as unknown[][] });
      addScript("evolution-clarity", `https://www.clarity.ms/tag/${analyticsConfig.clarityProjectId}`);
    });
    // Defer session recording until the browser has had a chance to paint the landing.
    if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(loadClarity, { timeout: 2000 });
    else window.setTimeout(loadClarity, 0);
  });
}

// Private by design: callers cannot send arbitrary parameters, answer values or error messages.
function trackEvent(event: EventName, parameters: Record<string, string | number> = {}): void {
  safely(() => {
    const path = pagePath();
    if (!hasGaId() || !path || !window.gtag) return;
    window.gtag("event", event, {
      ...parameters,
      ...pageContext(path),
      send_to: analyticsConfig.gaMeasurementId,
      ...(analyticsConfig.debug ? { debug_mode: true } : {}),
    });
  });
}

export function trackPageView(): void {
  safely(() => {
    const path = pagePath();
    if (!path || path === lastPage) return;
    lastPage = path;
    if (hasGaId()) window.gtag?.("set", pageContext(path));
    trackEvent("page_view");
  });
}

export function trackCtaClick(ctaId: CtaId): void {
  safely(() => {
    if (!Object.hasOwn(ctaDestinations, ctaId)) return;
    trackEvent("cta_click", { cta_id: ctaId, cta_destination: ctaDestinations[ctaId] });
  });
}

interface FormProgress {
  attempt: string;
  startedAt: number | null;
  viewed: number[];
  answered: number[];
  contactStarted: boolean;
  completed: boolean;
}
const PROGRESS_KEY = "evolution-analytics-progress-v1";
let progress: FormProgress | undefined;

function getProgress(attempt: string): FormProgress {
  if (progress?.attempt === attempt) return progress;
  let restored: FormProgress | undefined;
  safely(() => {
    const stored = JSON.parse(window.sessionStorage.getItem(PROGRESS_KEY) || "null") as FormProgress | null;
    const validSteps = (steps: unknown): steps is number[] => Array.isArray(steps)
      && steps.every((step) => Number.isInteger(step) && step >= 0 && step < QUESTION_COUNT);
    if (stored?.attempt === attempt && validSteps(stored.viewed) && validSteps(stored.answered)
      && (stored.startedAt === null || (Number.isFinite(stored.startedAt) && stored.startedAt > 0))
      && typeof stored.contactStarted === "boolean" && typeof stored.completed === "boolean") restored = stored;
  });
  progress = restored ?? { attempt, startedAt: null, viewed: [], answered: [], contactStarted: false, completed: false };
  return progress;
}

function persistProgress(state: FormProgress): void {
  // Local deduplication only. This ID and this object are never passed to either vendor.
  safely(() => window.sessionStorage.setItem(PROGRESS_KEY, JSON.stringify(state)));
}

function questionParameters(step: number) {
  const question = questionnaireQuestions[step];
  if (!question || !Number.isInteger(step)) return undefined;
  return { question_number: step + 1, question_id: question.id, question_name: question.id, question_type: question.type };
}

export function trackFormStarted(attempt: string): void {
  safely(() => {
    const state = getProgress(attempt);
    if (state.startedAt !== null || state.completed) return;
    state.startedAt = Date.now();
    persistProgress(state);
    trackEvent("form_started", { total_questions: QUESTION_COUNT });
  });
}

export function trackQuestionViewed(attempt: string, step: number): void {
  safely(() => {
    const parameters = questionParameters(step);
    if (!parameters) return;
    const state = getProgress(attempt);
    if (state.viewed.includes(step) || state.completed) return;
    state.viewed.push(step);
    persistProgress(state);
    trackEvent("question_viewed", parameters);
  });
}

export function trackQuestionAnswered(attempt: string, step: number): void {
  safely(() => {
    const parameters = questionParameters(step);
    if (!parameters) return;
    const state = getProgress(attempt);
    if (state.answered.includes(step) || state.completed) return;
    trackFormStarted(attempt);
    state.answered.push(step);
    persistProgress(state);
    trackEvent("question_answered", parameters);
  });
}

export function trackContactStarted(attempt: string): void {
  safely(() => {
    const state = getProgress(attempt);
    if (state.contactStarted || state.completed) return;
    state.contactStarted = true;
    persistProgress(state);
    trackEvent("form_contact_started", { total_questions: QUESTION_COUNT });
  });
}

export function trackFormCompleted(attempt: string): void {
  safely(() => {
    const state = getProgress(attempt);
    if (state.completed) return;
    state.completed = true;
    persistProgress(state);
    trackEvent("form_completed", {
      total_questions: QUESTION_COUNT,
      ...(state.startedAt === null ? {} : {
        form_duration_seconds: Math.max(0, Math.round((Date.now() - state.startedAt) / 1000)),
      }),
    });
  });
}

export function trackFormError(errorType: FormErrorType): void {
  if (["network", "http", "invalid_response"].includes(errorType)) trackEvent("form_error", { error_type: errorType });
}
